import { AlertTriangle, CheckCircle, Clock, FolderX, PauseCircle } from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";
import { geledenTekst } from "@/lib/datum";

/**
 * Wanneer de synchronisatielus voor het laatst keek, EN wat er toen gebeurde.
 * R18.
 *
 * FRANKS EIS VAN 22 AUGUSTUS 2026, EN ZIJ IS DE REDEN DAT DIT EEN EIGEN
 * COMPONENT IS EN GEEN PAAR REGELS IN `KnowledgePage`:
 *
 *   "last_sync_at en last_sync_result staan altijd naast elkaar, nooit los.
 *    Een verse tijdstempel zonder de uitslag ernaast leest als 'bij' terwijl
 *    dat niet zo hoeft te zijn."
 *
 * Die eis is hier een ONMOGELIJKHEID en geen afspraak. Deze component krijgt
 * de hele bank en rendert het paar; er is geen manier om alleen de tijdstempel
 * te tonen, want er is geen component die dat kan. Dat is de sterkere vorm:
 * waar een regel iets VRAAGT wat ook onmogelijk gemaakt kan worden, is
 * onmogelijk maken de sterkere vorm.
 *
 * WAAROM DIE EIS ER IS. `last_sync_at` is WANNEER de lus keek en niet wanneer
 * de bank is bijgewerkt. Hij wordt ook gezet als de bronmap onbereikbaar was
 * en als de bank op `sync_off` staat. Een tijdstempel van twee minuten geleden
 * kan dus horen bij een map die al een week weg is.
 *
 * DE TWEEDE EIS VAN DIEZELFDE DAG: `bronmap_onbereikbaar` en `bronmap_leeg`
 * horen HIER te staan en niet alleen in het log. Een gebruiker leest geen
 * serverlog, en een kennisbank die stil veroudert is precies wat R18 moest
 * oplossen.
 *
 * Engelse tekst, omdat het omringende kennisscherm Engels is. De schakelaars in
 * het gespreksscherm zijn Nederlands; die twee schermen lopen al uiteen en dit
 * is niet de rit om dat recht te trekken.
 */

/** De vier statuswoorden, letterlijk zoals `services/folder_sync.py` ze zet. */
export const OK = "ok";
export const ONBEREIKBAAR = "bronmap_onbereikbaar";
export const LEEG = "bronmap_leeg";
export const UITGEZET = "uitgezet";
export const MISLUKT_BINNENIN = "mislukt_binnenin";

export default function FolderSyncStatus({ kb }) {
  const { t, taal } = useTaal();
  // Geen bronmap, geen synchronisatie, en dus niets te melden. Een regel die
  // "never checked" zegt bij een bank van losse uploads suggereert een gebrek
  // dat er niet is.
  if (!kb?.source_folder) return null;

  const uitslag = kb.last_sync_result || null;
  const status = uitslag?.status || null;
  const wanneer = geledenTekst(kb.last_sync_at, t, taal);

  // Nog nooit gekeken. Dat is de normale toestand in het eerste kwartier na
  // het opstarten, en het hoort er te staan in plaats van te zwijgen: zwijgen
  // is niet te onderscheiden van een lus die niet draait.
  if (!kb.last_sync_at || !status) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="sync-status">
        <Clock className="h-3 w-3 shrink-0" />
        <span>{t("Automatisch bijwerken heeft nog niet naar deze kennisbank gekeken.")}</span>
      </p>
    );
  }

  if (status === ONBEREIKBAAR) {
    return (
      <p className="text-xs text-destructive flex items-start gap-1.5" data-testid="sync-status">
        <FolderX className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          <span className="font-medium">{t("Bronmap niet bereikbaar")}</span> {t("(laatst gekeken: {w}).", { w: wanneer })}
          {" "}{t("Er is niets uit deze kennisbank verwijderd.")}{" "}
          <span className="font-mono break-all">{kb.source_folder}</span>
        </span>
      </p>
    );
  }

  if (status === LEEG) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-500 flex items-start gap-1.5" data-testid="sync-status">
        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          <span className="font-medium">{t("Bronmap kwam leeg terug")}</span> {t("(laatst gekeken: {w}).", { w: wanneer })}
          {" "}{t("Er is niets verwijderd, want een losgekoppelde schijf ziet er precies zo uit als een leeggemaakte map.")}
        </span>
      </p>
    );
  }

  if (status === MISLUKT_BINNENIN) {
    // HET VIJFDE WOORD, en de tekst stuurt bewust WEG van de map. De andere
    // twee foutregels wijzen naar je Dropbox; deze zou daar een middag zoeken
    // opleveren naar iets wat er niet is. Er staat ook bij dat er niets half is
    // vastgelegd, want dat is de vraag die je bij zo'n melding als eerste stelt.
    return (
      <p className="text-xs text-destructive flex items-start gap-1.5" data-testid="sync-status">
        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          <span className="font-medium">{t("Bijwerken mislukte binnen de app")}</span>{" "}
          {t("(laatst gekeken: {w}), en niet door de map. Er is niets half blijven staan; deze ronde is teruggedraaid.", { w: wanneer })}
          {uitslag.reden && <span className="font-mono break-all"> {uitslag.reden}</span>}
        </span>
      </p>
    );
  }

  if (status === UITGEZET) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="sync-status">
        <PauseCircle className="h-3 w-3 shrink-0" />
        <span>{t("Automatisch bijwerken staat uit voor deze kennisbank. Laatst gekeken: {w}.", { w: wanneer })}</span>
      </p>
    );
  }

  // OK. De aantallen erbij, want "checked" alleen zegt niet of er iets
  // veranderde, en dat is juist wat je wilt weten.
  const nieuw = uitslag.toegevoegd || 0;
  const gewijzigd = uitslag.bijgewerkt || 0;
  const weg = (uitslag.verwijderd || []).length;
  const mislukt = (uitslag.mislukt || []).length;
  const geraakt = nieuw + gewijzigd + weg;

  return (
    <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="sync-status">
      <CheckCircle className="h-3 w-3 shrink-0 mt-0.5" />
      <span>
        {geraakt === 0
          ? t("Bijgewerkt {w}, er was niets veranderd.", { w: wanneer })
          : t("Bijgewerkt {w}: {nieuw} nieuw, {gewijzigd} bijgewerkt, {weg} verwijderd.", { w: wanneer, nieuw, gewijzigd, weg })}
        {mislukt > 0 && (
          <span className="text-destructive"> {t("{n} bestand(en) konden niet worden gelezen.", { n: mislukt })}</span>
        )}
      </span>
    </p>
  );
}
